import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { firstValueFrom, Subscription, timeout } from 'rxjs';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { environment } from '../../../environments/environment';
import { ClientLogEventType, LogBatchResponse, LogClientContext, LogSessionContext, QueuedLogEvent } from '../../shared/models/be/system-log';
import { LogEventStore } from './log-event-store.service';
import { RequestsService } from './utils/requests.service';
import { NetworkStatusService } from './network-status.service';
import { createUuid } from '../../shared/utils/uuid.util';

const CONTEXT_KEY = 'WITA_LOG_CONTEXT';
const CAPABILITIES_KEY = 'WITA_LOG_CAPABILITIES';
export const LOG_INSTALLATION_KEY = 'WITA_LOG_INSTALLATION_ID';

@Injectable({ providedIn: 'root' })
export class ClientLogsService implements OnDestroy {
  private initialized = false;
  private frozen = false;
  private authBlocked = false;
  private online = true;
  private backendUnavailable = false;
  private timer?: ReturnType<typeof setTimeout>;
  private polling?: ReturnType<typeof setInterval>;
  private subscriptions = new Subscription();
  private nativeListener?: { remove(): Promise<void> };
  private upload?: Promise<void>;
  private retryCount = 0;
  private batchSize = 50;
  private notBefore = 0;
  private readonly foreground = () => {
    if (document.visibilityState === 'visible') { this.schedule(0); void this.probe(); }
  };

  constructor(private http: HttpClient, private store: LogEventStore, private requests: RequestsService,
    private network: NetworkStatusService, private zone: NgZone) {}

  clientContext(): LogClientContext {
    let installationId = localStorage.getItem(LOG_INSTALLATION_KEY);
    if (!installationId) {
      installationId = createUuid();
      localStorage.setItem(LOG_INSTALLATION_KEY, installationId);
    }
    // Existing display versions such as "1.9" become a valid semantic version.
    const appVersion = /^\d+\.\d+$/.test(environment.appVersion) ? `${environment.appVersion}.0` : environment.appVersion;
    return { installationId, appVersion, platform: Capacitor.isNativePlatform() ? 'android_app' : 'desktop_web' };
  }

  startSession(userId: number | string, sessionId: string | undefined, context: LogClientContext,
    capabilities?: { applicationErrorDetails?: boolean }): void {
    localStorage.removeItem(CONTEXT_KEY);
    localStorage.removeItem(CAPABILITIES_KEY);
    if (sessionId && Number.isSafeInteger(Number(userId)) && Number(userId) > 0) {
      localStorage.setItem(CONTEXT_KEY, JSON.stringify({ ...context, userId: Number(userId), sessionId } satisfies LogSessionContext));
      if (capabilities?.applicationErrorDetails === true) {
        localStorage.setItem(CAPABILITIES_KEY, JSON.stringify({ sessionId, applicationErrorDetails: true }));
      }
    }
    this.frozen = false;
    this.authBlocked = false;
    this.backendUnavailable = false;
    this.retryCount = 0;
  }

  supportsApplicationErrorDetails(): boolean {
    try {
      const context = this.context();
      const raw = localStorage.getItem(CAPABILITIES_KEY);
      const capabilities = raw ? JSON.parse(raw) : undefined;
      return !!context && capabilities?.sessionId === context.sessionId && capabilities?.applicationErrorDetails === true;
    } catch { return false; }
  }

  initialize(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.subscriptions.add(this.network.isOnline$.subscribe(online => {
      this.online = online;
      const context = this.context();
      if (context) void this.store.transition(context, 'network', online).then(() => {
        if (online) { this.schedule(0); void this.probe(); }
      }).catch(() => this.storageFailure());
    }));
    this.subscriptions.add(this.requests.currentUser$.subscribe(() => this.authenticationAvailable()));
    document.addEventListener('visibilitychange', this.foreground);
    if (Capacitor.isNativePlatform()) {
      void App.addListener('appStateChange', state => {
        if (state.isActive) { this.schedule(0); void this.probe(); }
      }).then(listener => this.nativeListener = listener).catch(() => undefined);
    }
    this.zone.runOutsideAngular(() => {
      this.polling = setInterval(() => {
        this.schedule(0);
        if (this.backendUnavailable && document.visibilityState !== 'hidden') void this.probe();
      }, 30_000);
    });
  }

  authenticationAvailable(): void {
    this.authBlocked = false;
    const context = this.context();
    if (!context) return;
    void this.store.transition(context, 'network', this.online).catch(() => this.storageFailure());
    this.schedule(0);
  }

  /** Freeze production before logout; keep historical events for the same owner. */
  async prepareForLogout(): Promise<void> {
    this.frozen = true;
    await this.flush(true);
  }

  resumeAfterFailedLogout(): void { this.frozen = false; this.authenticationAvailable(); }

  captureContext(): LogSessionContext | undefined { return this.context(); }

  observeBackend(status: number, observedContext?: LogSessionContext): void {
    const context = this.context();
    if (!context || !observedContext || context.sessionId !== observedContext.sessionId || context.userId !== observedContext.userId || !this.online || status === 401 || status === 403 || status === 429) return;
    if (status === 0 || status >= 500) {
      this.backendUnavailable = true;
      void this.store.transition(context, 'backend', false, {
        errorCode: status === 0 ? 'UNKNOWN' : 'HTTP_ERROR', ...(status >= 500 ? { httpStatus: status } : {})
      }).then(() => this.schedule()).catch(() => this.storageFailure());
    } else if (status >= 200 && status < 500) {
      this.backendUnavailable = false;
      void this.store.transition(context, 'backend', true).then(() => this.schedule()).catch(() => this.storageFailure());
    }
  }

  record(eventType: ClientLogEventType, metadata: Record<string, string | number>, reasonCode?: string, observedContext?: LogSessionContext): void {
    const context = this.context();
    if (!context) return;
    if (observedContext && (context.sessionId !== observedContext.sessionId || context.userId !== observedContext.userId)) return;
    const { userId, ...clientContext } = context;
    void this.store.add(userId, { ...clientContext, id: createUuid(), eventType,
      occurredAt: new Date().toISOString(), metadata, ...(reasonCode ? { reasonCode } : {})
    }).then(() => this.schedule()).catch(() => this.storageFailure());
  }

  private context(allowFrozen = false): LogSessionContext | undefined {
    try {
      if ((!allowFrozen && this.frozen) || !localStorage.getItem('ACCESS_TOKEN')) return undefined;
      const raw = localStorage.getItem(CONTEXT_KEY);
      const context = raw ? JSON.parse(raw) as LogSessionContext : undefined;
      if (context?.userId !== this.requests.getCurrentUser()?.id) return undefined;
      return context;
    } catch { return undefined; }
  }

  private schedule(delay = 1000): void {
    if (!this.initialized || this.frozen || this.authBlocked || !this.online || !this.context()) return;
    if (this.timer) return;
    this.zone.runOutsideAngular(() => {
      this.timer = setTimeout(() => {
        this.timer = undefined;
        void this.flush();
      }, Math.max(delay, this.notBefore - Date.now()));
    });
  }

  async flush(allowFrozen = false): Promise<void> {
    if (this.upload) return this.upload;
    if (!this.online || this.authBlocked || Date.now() < this.notBefore || !this.context(allowFrozen)) return;
    const run = async () => {
      if (navigator.locks) {
        await navigator.locks.request('wita-system-logs-upload', { ifAvailable: true }, async lock => {
          if (lock) await this.sendBatch(allowFrozen);
        });
      } else await this.sendBatch(allowFrozen);
    };
    this.upload = run().catch(() => this.storageFailure()).finally(() => { this.upload = undefined; });
    return this.upload;
  }

  private async sendBatch(allowFrozen: boolean): Promise<void> {
    const context = this.context(allowFrozen);
    if (!context) return;
    const pending = await this.store.pending(context.userId, this.batchSize);
    const rows: QueuedLogEvent[] = [];
    const oversized = new Map<string, string>();
    let bytes = 32;
    for (const row of pending) {
      const size = new TextEncoder().encode(JSON.stringify(row.event)).length;
      if (size > 4096) { oversized.set(row.id, 'INVALID_EVENT'); continue; }
      if (bytes + size + 1 > 120 * 1024) break;
      bytes += size + 1;
      rows.push(row);
    }
    if (oversized.size) await this.store.settle(pending, new Set(), oversized);
    if (!rows.length || this.context(allowFrozen)?.userId !== context.userId) return;
    try {
      const response = await firstValueFrom(this.http.post<LogBatchResponse>(`${environment.apiEndpoint}/logs/batch`,
        { events: rows.map(row => row.event) }).pipe(timeout(4500)));
      const remove = new Set<string>();
      const rejected = new Map<string, string>();
      const seen = new Set<number>();
      for (const result of response.results ?? []) {
        const row = rows[result.index];
        if (!row || seen.has(result.index) || (result.clientEventId !== row.id && !(result.status === 'rejected' && result.clientEventId === null))) continue;
        seen.add(result.index);
        if (result.status === 'accepted' || result.status === 'duplicate') remove.add(row.id);
        else if (result.status === 'rejected') rejected.set(row.id, result.code ?? 'INVALID_EVENT');
      }
      await this.store.settle(rows, remove, rejected);
      const hasUnconfirmed = remove.size + rejected.size !== rows.length;
      if (hasUnconfirmed) this.backoff();
      else { this.retryCount = 0; this.schedule(2200); }
    } catch (error) {
      const httpError = error instanceof HttpErrorResponse ? error : undefined;
      if (httpError?.status === 401 || httpError?.status === 403) { this.authBlocked = true; return; }
      if (httpError?.status === 413) {
        if (rows.length === 1) await this.store.settle(rows, new Set(), new Map([[rows[0].id, 'INVALID_EVENT']]));
        else this.batchSize = Math.max(1, Math.floor(rows.length / 2));
      } else if (httpError?.status === 400) {
        await this.store.settle(rows, new Set(), new Map(rows.map(row => [row.id, 'INVALID_BATCH'])));
      }
      const retryAfter = httpError?.status === 429 ? httpError.headers.get('Retry-After') : null;
      this.backoff(retryAfter);
    }
  }

  private backoff(retryAfter?: string | null): void {
    const serverDelay = retryAfter ? (/^\d+$/.test(retryAfter) ? Number(retryAfter) * 1000 : Date.parse(retryAfter) - Date.now()) : 0;
    const delay = Math.max(Number.isFinite(serverDelay) ? serverDelay : 0,
      Math.min(60_000, 2200 * 2 ** Math.min(this.retryCount++, 5)) + Math.random() * 1000);
    this.notBefore = Date.now() + delay;
    this.schedule(delay);
  }

  private async probe(): Promise<void> {
    if (!this.online || !this.context() || document.visibilityState === 'hidden') return;
    try { await firstValueFrom(this.http.get(`${environment.apiEndpoint}/auth/ping`).pipe(timeout(5000))); }
    catch { /* The connectivity interceptor observes transport/HTTP failures; no recursive logging. */ }
  }

  private storageFailure(): void {
    // Never feed failures of this logger back into APPLICATION_ERROR.
    console.warn('LOGS_STORAGE_UNAVAILABLE');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.timer) clearTimeout(this.timer);
    if (this.polling) clearInterval(this.polling);
    document.removeEventListener('visibilitychange', this.foreground);
    void this.nativeListener?.remove();
  }
}
